import { api } from './api';

export interface TutorSessionDTO {
  id: number;
  courseId: number;
  tutorId: number;
  status: 'ACTIVE' | 'ENDED';
  currentTopicId: number | null;
  currentPageIndex: number;
  lastSeq: number;
  startedAt: string;
  endedAt: string | null;
}

export async function startSession(courseId: number): Promise<TutorSessionDTO> {
  const response = await api.post<{ data: TutorSessionDTO }>(
    `/api/tutor/courses/${courseId}/session/start`
  );
  return response.data.data;
}

export async function endSession(courseId: number): Promise<TutorSessionDTO> {
  const response = await api.post<{ data: TutorSessionDTO }>(
    `/api/tutor/courses/${courseId}/session/end`
  );
  return response.data.data;
}

export async function updateSessionPage(
  courseId: number,
  topicId: number | null,
  pageIndex: number
): Promise<TutorSessionDTO> {
  const response = await api.patch<{ data: TutorSessionDTO }>(
    `/api/tutor/courses/${courseId}/session/page`,
    { topicId, pageIndex }
  );
  return response.data.data;
}

export async function getActiveSession(courseId: number): Promise<TutorSessionDTO | null> {
  try {
    const response = await api.get<{ data: TutorSessionDTO }>(
      `/api/tutor/courses/${courseId}/session/active`
    );
    return response.data.data;
  } catch (err: any) {
    if (err?.response?.status === 204 || err?.response?.status === 404) return null;
    throw err;
  }
}

// ─── Live Quiz ────────────────────────────────────────────────────────────────

export interface LiveQuizSessionDTO {
  id: number;
  courseId: number;
  topicNodeId: number | null;
  questionIds: number[];
  timerSeconds: number;
  startedAt: string;
  status: 'ACTIVE' | 'CLOSED';
}

export interface LiveQuizResultsDTO {
  quizSessionId: number;
  studentResults: StudentQuizResult[];
}

export interface StudentQuizResult {
  studentId: number;
  studentName: string;
  submittedAt: string;
  answers: AnswerDetail[];
  score: number;
}

export interface AnswerDetail {
  questionId: number;
  selectedOptionId: number;
  isCorrect: boolean;
}

export async function dispatchLiveQuiz(
  courseId: number,
  topicNodeId: number | null,
  questionIds: number[],
  timerSeconds: number
): Promise<LiveQuizSessionDTO> {
  const response = await api.post<{ data: LiveQuizSessionDTO }>(
    `/api/tutor/courses/${courseId}/session/quiz/dispatch`,
    { topicNodeId, questionIds, timerSeconds }
  );
  return response.data.data;
}

export async function closeLiveQuiz(courseId: number): Promise<LiveQuizSessionDTO> {
  const response = await api.post<{ data: LiveQuizSessionDTO }>(
    `/api/tutor/courses/${courseId}/session/quiz/close`
  );
  return response.data.data;
}

export async function getLiveQuizResults(
  courseId: number,
  quizSessionId: number
): Promise<LiveQuizResultsDTO> {
  const response = await api.get<{ data: LiveQuizResultsDTO }>(
    `/api/tutor/courses/${courseId}/session/quiz/${quizSessionId}/results`
  );
  return response.data.data;
}

// ─── Taught Records ───────────────────────────────────────────────────────────

export interface TaughtRecordDTO {
  id: number;
  nodeId: number;
  courseId: number;
  tutorId: number;
  isTaught: boolean;
  taughtAt: string | null;
}

export async function getTaughtRecords(courseId: number): Promise<TaughtRecordDTO[]> {
  const response = await api.get<{ data: TaughtRecordDTO[] }>(
    `/api/tutor/courses/${courseId}/nodes/taught`
  );
  return response.data.data;
}

export async function toggleTaughtStatus(courseId: number, nodeId: number): Promise<TaughtRecordDTO> {
  const response = await api.patch<{ data: TaughtRecordDTO }>(
    `/api/tutor/courses/${courseId}/nodes/${nodeId}/taught`
  );
  return response.data.data;
}
